const _ = require("lodash");
const PT = require("prop-types");
const React = require("react");
const OfficeModal = require("../../office/modals/OfficeModal");
const WonkyButton = require("./WonkyButton");
const AssetsUtil = require("../util/AssetsUtil");
const Mixpanel = require("../util/Mixpanel");
const AT = require("../vault/ActionTypes");
const withVaultDispatch = require("../hoc/withVaultDispatch");
const {PORTAL_CONTAINERS} = require("../../../shared/PortalConstants");

const styles = require("./styles/PlacementTestInviteModal.css");

const PLACEMENT_TEST_VIRTUAL_URL =
	"https://artofproblemsolving.com/placement/virtual?source=BA";

function getButtonScales() {
	let rootPx = 8;
	if (typeof document !== "undefined") {
		const parsed = parseFloat(
			global.getComputedStyle(document.documentElement).fontSize
		);
		if (_.isFinite(parsed) && parsed > 0) {
			rootPx = parsed;
		}
	}
	return {
		noThanks: 261 / (16 * rootPx),
		letsGo: 252 / (15 * rootPx),
	};
}

/**
 * Placement test invite — used from home and chapter when vault emits
 * showPlacementTestInviteModal. Persists notifications.placementTestInviteModal
 * to "shown" only when the student dismisses (CTAs, close, Escape — not while
 * "Let's go" is blocked by the browser).
 */
function PlacementTestInviteModal(props) {
	const {isOpen, onClose, dispatch} = props;

	const [popupBlocked, setPopupBlocked] = React.useState(false);

	const placementInviteModalOpenEffectsAppliedRef = React.useRef(false);
	const placementTestInviteModalKeydownRef = React.useRef(null);
	const onCloseRef = React.useRef(onClose);
	const dispatchRef = React.useRef(dispatch);

	onCloseRef.current = onClose;
	dispatchRef.current = dispatch;

	const notifyInviteDismissed = React.useCallback(() => {
		dispatchRef.current({
			type: AT.UPDATE_USER_EVENTS_TRIGGERED,
			pathArray: ["notifications", "placementTestInviteModal"],
			propValue: "shown",
		});
	}, []);

	const notifyInviteDismissedRef = React.useRef(notifyInviteDismissed);
	notifyInviteDismissedRef.current = notifyInviteDismissed;

	React.useEffect(() => {
		const keydownHandler = function(e) {
			if (e.key === "Escape" && !e.defaultPrevented) {
				Mixpanel.track("Placement Test Invite Modal Dismissed", {
					method: "escape",
				});
				notifyInviteDismissedRef.current();
				onCloseRef.current();
			}
		};

		if (isOpen) {
			// can be triggered milliseconds before actual render, or if a JS error prevents rendering
			Mixpanel.track("Placement Test Invite Modal Viewed");
			placementTestInviteModalKeydownRef.current = keydownHandler;
			document.addEventListener("keydown", keydownHandler);
		} else {
			if (placementTestInviteModalKeydownRef.current) {
				document.removeEventListener(
					"keydown",
					placementTestInviteModalKeydownRef.current
				);
			}
			setPopupBlocked(false); // closing the popup would unblock it always i guess.
			placementInviteModalOpenEffectsAppliedRef.current = false;
			placementTestInviteModalKeydownRef.current = null;
		}

		return () => {
			document.removeEventListener(
				"keydown",
				placementTestInviteModalKeydownRef.current
			);
		};
	}, [isOpen]);

	const closeFromBackdropOrX = React.useCallback(() => {
		Mixpanel.track("Placement Test Invite Modal Dismissed", {
			method: "backdrop_or_close",
		});
		notifyInviteDismissed();
		onCloseRef.current();
	}, [notifyInviteDismissed]);

	const onNoThanks = React.useCallback(() => {
		Mixpanel.track("Placement Test Invite Modal Dismissed", {
			method: "no_thanks",
		});
		notifyInviteDismissed();
		onCloseRef.current();
	}, [notifyInviteDismissed]);

	const onPrimary = React.useCallback(() => {
		Mixpanel.track("Placement Test Invite Modal Action", {
			action: "lets_go",
		});
		if (typeof global.window !== "undefined") {
			const w = global.window.open(PLACEMENT_TEST_VIRTUAL_URL, "_blank");
			if (w) {
				w.opener = null;
				setPopupBlocked(false);
				notifyInviteDismissed();
				onCloseRef.current();
				return;
			}
			Mixpanel.track("Placement Test Invite Modal Tab Open Blocked", {
				reason: "popup_blocked",
			});
			setPopupBlocked(true);
			return;
		}
		notifyInviteDismissed();
		onCloseRef.current();
	}, [notifyInviteDismissed]);

	if (!isOpen) {
		return null;
	}
	const scales = getButtonScales();
	return (
		<OfficeModal
			modalClass={`${styles.placementTestInviteModal} ${styles.placementTestInviteModalOfficeMain}`}
			closeClass={`${styles.placementTestInviteModalClose} ${styles.placementTestInviteModalCloseArt}`}
			onClose={closeFromBackdropOrX}
			closeOnEsc={false}
			portalContainer={PORTAL_CONTAINERS.HOME_OVERLAY_MODAL}
			/* above SequenceSlideout — portal mounts after slideout in DOM */
			level="warning"
			closeOnBackdropClick={false}
			isScrollable={false}
			isFixed={true}
		>
			<div className={styles.placementTestInviteModalContainer}>
				<img
					className={styles.placementTestInviteModalImage}
					src={AssetsUtil.getPlacementTestInviteModalImage()}
				/>
				<div className={styles.placementTestInviteModalTitle}>
					Psst...Want to help us build something new?
				</div>
				<div className={styles.placementTestInviteModalMessage}>
					We're creating a new test and picked just a few students to try it
					out. It takes about 20 minutes. Totally optional. Tell us what you
					think!
				</div>

				{popupBlocked ? (
					<div className={styles.placementTestInviteModalPopupHint}>
						We couldn't open a new tab (your browser may block popups). Try
						&quot;Let's go&quot; again after allowing popups, or{" "}
						<a
							href={PLACEMENT_TEST_VIRTUAL_URL}
							target="_blank"
							rel="noopener noreferrer"
						>
							open the placement test
						</a>{" "}
						in a new tab.
					</div>
				) : null}
			</div>
			<div className={styles.placementTestInviteModalButtonRow}>
				<WonkyButton
					buttonType="trapNormalWide"
					buttonText="No thanks"
					variant="outline"
					scale={scales.noThanks}
					noShadow={true}
					handleClick={onNoThanks}
				/>
				<WonkyButton
					buttonType="trapFlippedWide"
					buttonText="Let's go"
					scale={scales.letsGo}
					noShadow={true}
					handleClick={onPrimary}
				/>
			</div>
		</OfficeModal>
	);
}

PlacementTestInviteModal.displayName = "PlacementTestInviteModal";

PlacementTestInviteModal.propTypes = {
	isOpen: PT.bool.isRequired,
	onClose: PT.func.isRequired,
	dispatch: PT.func.isRequired,
};

module.exports = withVaultDispatch(PlacementTestInviteModal);
